'use client';
import React, { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';

import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../../components/ui/card';
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from '../../../components/ui/chart';
import { TollGateData } from '../../../modules/control-tower/api';
import { format, isDate, isPast, isToday, isTomorrow, isYesterday } from 'date-fns';

const chartConfig = {
  beforeThreshold: {
    label: 'Before threshold',
    color: 'hsl(var(--chart-ct-before))',
  },
  afterThreshold: {
    label: 'After threshold',
    color: 'hsl(var(--chart-ct-after))',
  },
  attemptBeforeThreshold: {
    label: 'Attempt before threshold',
    color: 'hsl(var(--chart-ct-attempt-before))',
  },
  attemptAfterThreshold: {
    label: 'Attempt after threshold',
    color: 'hsl(var(--chart-ct-attempt-after))',
  },
  restStillTime: {
    label: 'Still to expect',
    color: 'hsl(var(--chart-ct-wait))',
  },
  restTooLate: {
    label: 'Missed tollgate',
    color: 'hsl(var(--chart-ct-missed))',
  },
} satisfies ChartConfig;
export interface ControlTowerGraphProps {
  day: string;
  data: TollGateData[];
  onDrillClick: (v: { key: string; day: string; threshold: string }) => void;
  xlabel: string;
}

const Tick = (props) => {
  const { x, width, y, payload } = props;
  const xUsed = x - width / 2;
  const [a, b] = payload.value.split('|');
  return (
    <text x={xUsed} y={y}>
      <tspan x={xUsed} dy="0" className="font-bold">
        {a}
      </tspan>
      <tspan x={xUsed} dy="1.4em">
        {b}
      </tspan>
    </text>
  );
};

const getDayName = (day: string) => {
  if (!day) return 'n/a';
  const date = new Date(day);

  if (isNaN(date.getTime())) {
    return 'n/a';
  }
  if (isToday(date)) {
    return 'today';
  }
  if (isYesterday(date)) {
    return 'yesterday';
  }
  if (isTomorrow(date)) {
    return 'tomorrow';
  }
  return isPast(day) ? `last ${format(date, 'EEEE')}` : `next ${format(date, 'EEEE')}`;
};
const capitalize = (s: string) => `${s[0].toUpperCase()}${s.slice(1)}`;

export function ControlTowerGraph({ day, data, onDrillClick, xlabel }: ControlTowerGraphProps) {
  const onBarClick = (key: string) => (v) => onDrillClick({ key, day: v.day, threshold: v.threshold });

  const noOrdersForThisDay = useMemo(
    () => data?.every((l) => Object.entries(l)?.every(([k, v]) => k === 'day' || typeof v === 'string' || v === 0)),
    [data],
  );
  const dayName = getDayName(day);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{capitalize(dayName)}</CardTitle>
        <CardDescription>Orders with delivery date of {dayName}. Showing their tollgate performance</CardDescription>
      </CardHeader>
      <CardContent>
        {noOrdersForThisDay ? (
          <p className="text-sm text-gray-500">No orders for this day (yet).</p>
        ) : (
          <>
            <ChartContainer config={chartConfig}>
              <BarChart accessibilityLayer data={data} layout="vertical">
                <CartesianGrid vertical={true} horizontal={false} />
                <XAxis
                  type="number"
                  allowDecimals={true}
                  alignmentBaseline="auto"
                  label={{
                    value: xlabel,
                    position: 'insideBottom',
                    offset: 0,
                  }}
                />

                <YAxis
                  width={100}
                  dataKey="name"
                  type="category"
                  tickLine={false}
                  tickMargin={-30}
                  axisLine={false}
                  tickSize={80}
                  tick={Tick}
                />
                <ChartTooltip cursor={false} content={<ChartTooltipContent />} />

                <Bar
                  className="cursor-pointer"
                  dataKey="beforeThreshold"
                  stackId="a"
                  fill="var(--color-beforeThreshold)"
                  radius={4}
                  onClick={onBarClick('beforeThreshold')}
                />
                <Bar
                  className="cursor-pointer"
                  dataKey="attemptBeforeThreshold"
                  stackId="a"
                  fill="var(--color-attemptBeforeThreshold)"
                  radius={4}
                  onClick={onBarClick('attemptBeforeThreshold')}
                />
                <Bar
                  className="cursor-pointer"
                  dataKey="afterThreshold"
                  stackId="a"
                  fill="var(--color-afterThreshold)"
                  radius={4}
                  onClick={onBarClick('afterThreshold')}
                />
                <Bar
                  className="cursor-pointer"
                  dataKey="attemptAfterThreshold"
                  stackId="a"
                  fill="var(--color-attemptAfterThreshold)"
                  radius={4}
                  onClick={onBarClick('attemptAfterThreshold')}
                />
                <Bar
                  className="cursor-pointer"
                  dataKey="restStillTime"
                  stackId="a"
                  fill="var(--color-restStillTime)"
                  radius={4}
                  onClick={onBarClick('restStillTime')}
                />
                <Bar
                  className="cursor-pointer"
                  dataKey="restTooLate"
                  stackId="a"
                  fill="var(--color-restTooLate)"
                  radius={4}
                  onClick={onBarClick('restTooLate')}
                />
              </BarChart>
            </ChartContainer>
          </>
        )}
      </CardContent>
      <CardFooter className="flex-col items-start gap-2 text-sm"></CardFooter>
    </Card>
  );
}
